import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/apiService';
import DeleteIcon from '@mui/icons-material/Delete';
import { Upload } from 'lucide-react';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4'];

// Trim, collapse slashes, strip leading/trailing slashes, max 100 chars.
const sanitizeFolderInput = (raw) =>
  (raw || '').trim().replace(/\/+/g, '/').replace(/^\/|\/$/, '').substring(0, 100) || null;

// ── Build nested tree from flat sorted folder paths ──────────────────────────
function buildFolderTree(folders) {
  const root = {};
  for (const p of folders) {
    const parts = p.split('/');
    let node = root;
    for (const part of parts) {
      if (!node[part]) node[part] = {};
      node = node[part];
    }
  }
  function toArray(obj, prefix) {
    return Object.keys(obj).sort().map(key => {
      const path = prefix ? `${prefix}/${key}` : key;
      const children = toArray(obj[key], path);
      return { name: key, path, children };
    });
  }
  return toArray(root, '');
}

// ── Recursive tree node ───────────────────────────────────────────────────────
const TreeNode = ({ node, depth, activeFolder, onSelect, onDelete, expandedSet, toggleExpand }) => {
  const isActive = activeFolder === node.path;
  const isExpanded = expandedSet.has(node.path);
  const hasChildren = node.children.length > 0;
  const base = {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: `5px 8px 5px ${8 + depth * 16}px`,
    borderRadius: 6, margin: '1px 0', cursor: 'pointer',
    background: isActive ? '#eff6ff' : 'transparent',
    color: isActive ? '#1d4ed8' : '#374151',
    fontWeight: isActive ? 600 : 400,
    fontSize: 13, userSelect: 'none',
    transition: 'background 0.1s',
  };
  return (
    <>
      <div
        style={{ ...base, paddingRight: 4 }}
        onClick={() => { onSelect(node.path); if (hasChildren) toggleExpand(node.path); }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: 9, width: 10, textAlign: 'center', color: '#9ca3af', flexShrink: 0 }}>
          {hasChildren ? (isExpanded ? '▼' : '▶') : ''}
        </span>
        <span>📁</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={node.path}>
          {node.name}
        </span>
        {/* Delete button — visible on hover of the row */}
        <span
          title={`Elimina cartella “${node.path}”`}
          onClick={e => { e.stopPropagation(); onDelete(node.path); }}
          style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 11, color: '#9ca3af', padding: '0 2px', lineHeight: 1, borderRadius: 3, cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
        >
          ✕
        </span>
      </div>
      {hasChildren && isExpanded && node.children.map(child => (
        <TreeNode
          key={child.path}
          node={child}
          depth={depth + 1}
          activeFolder={activeFolder}
          onSelect={onSelect}
          onDelete={onDelete}
          expandedSet={expandedSet}
          toggleExpand={toggleExpand}
        />
      ))}
    </>
  );
};

// Simple flat row used for "Tutte" and "Senza cartella" in the sidebar
const SidebarRow = ({ label, icon, depth, active, muted, onClick }) => {
  const base = {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: `5px 8px 5px ${8 + depth * 16}px`,
    borderRadius: 6, margin: '1px 0', cursor: 'pointer',
    background: active ? '#eff6ff' : 'transparent',
    color: active ? '#1d4ed8' : muted ? '#9ca3af' : '#374151',
    fontWeight: active ? 600 : 400,
    fontSize: 13, userSelect: 'none',
  };
  return (
    <div
      style={base}
      onClick={onClick}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f3f4f6'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: 9, width: 10 }} />
      <span>{icon}</span>
      <span>{label}</span>
    </div>
  );
};

const MediaAssets = () => {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);       // distinct folder paths from server
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tenantIdFilter, setTenantIdFilter] = useState('');
  // null = all, '__none__' = no folder, string = prefix filter (shows folder + all sub-paths)
  const [activeFolder, setActiveFolder] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [lightbox, setLightbox] = useState(null);
  const [movingAssetId, setMovingAssetId] = useState(null);
  const [moveTarget, setMoveTarget] = useState('');   // '' = no folder, '__new__' = create new
  const [moveIsNew, setMoveIsNew] = useState(false);
  const [moveNewFolderName, setMoveNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());  // bulk selection
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const folderTree = buildFolderTree(folders);

  const toggleExpand = useCallback((path) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const res = await apiService.getMediaFolders();
      setFolders(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  const deleteFolder = useCallback(async (path) => {
    if (!window.confirm(`Eliminare la cartella "${path}"?\n\nL'operazione è bloccata se contiene ancora asset.`)) return;
    try {
      await apiService.deleteMediaFolder(path);
      if (activeFolder === path || activeFolder?.startsWith(`${path}/`)) setActiveFolder(null);
      await loadFolders();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert(`⛔ ${msg}`);
    }
  }, [activeFolder, loadFolders]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (tenantIdFilter) params.tenantId = tenantIdFilter;
      if (activeFolder === '__none__') {
        params.folder = '__none__';
      } else if (activeFolder) {
        // folderPrefix returns assets in activeFolder AND all sub-paths
        params.folderPrefix = activeFolder;
      }
      const res = await apiService.getMediaAssets(params);
      setAssets(res.data || []);
      setSelectedIds(new Set()); // clear selection on reload
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantIdFilter, activeFolder]);

  // ── Bulk selection helpers ──────────────────────────────────────────
  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(assets.map(a => a.id)));
  }, [assets]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkDelete = useCallback(async () => {
    const ids = [...selectedIds];
    if (!window.confirm(`Eliminare ${ids.length} asset selezionat${ids.length === 1 ? 'o' : 'i'}? L'operazione non è reversibile.`)) return;
    setBulkDeleting(true);
    const blocked = [];
    const errors = [];
    for (const id of ids) {
      try {
        await apiService.deleteMediaAsset(id);
      } catch (err) {
        const data = err.response?.data;
        if (data?.usedInProfiles?.length > 0) {
          const asset = assets.find(a => a.id === id);
          blocked.push({ name: asset?.fileName || id, profiles: data.usedInProfiles });
        } else {
          errors.push(data?.error || err.message);
        }
      }
    }
    setBulkDeleting(false);
    await Promise.all([loadFolders(), load()]);
    if (blocked.length > 0 || errors.length > 0) {
      let msg = '';
      if (blocked.length > 0) {
        msg += `⛔ ${blocked.length} asset non eliminat${blocked.length === 1 ? 'o' : 'i'} perché in uso:\n`;
        blocked.forEach(b => { msg += `  • ${b.name} (${b.profiles.map(p => p.namespace).join(', ')})\n`; });
      }
      if (errors.length > 0) msg += `\nAltri errori (${errors.length}): ${errors[0]}`;
      alert(msg.trim());
    }
  }, [selectedIds, assets, loadFolders, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert(`Tipo non supportato: ${file.type}. Consentiti: ${ALLOWED_TYPES.join(', ')}`);
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      if (tenantIdFilter) formData.append('tenantId', tenantIdFilter);
      // Upload into the active folder (if a specific one is selected)
      if (activeFolder && activeFolder !== '__none__') {
        formData.append('folder', activeFolder);
      }
      await apiService.uploadMediaAsset(formData);
      await Promise.all([loadFolders(), load()]);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare questo asset? L\'operazione non è reversibile.')) return;
    try {
      await apiService.deleteMediaAsset(id);
      await Promise.all([loadFolders(), load()]);
    } catch (err) {
      const data = err.response?.data;
      if (data?.usedInProfiles?.length > 0) {
        const list = data.usedInProfiles.map(p => `  • ${p.namespace} (${p.status})`).join('\n');
        alert(`⛔ Impossibile eliminare l'asset.\n\nÈ ancora utilizzato da ${data.usedInProfiles.length} configurazione/i:\n${list}\n\nRimuovi l'asset da quelle configurazioni prima di eliminarlo.`);
      } else {
        alert(data?.error || err.message);
      }
    }
  };

  const startMove = (asset) => {
    setMovingAssetId(asset.id);
    setMoveTarget(asset.folder || '');
    setMoveIsNew(false);
    setMoveNewFolderName('');
  };

  const handleMoveSelectChange = (e) => {
    const v = e.target.value;
    setMoveTarget(v);
    setMoveIsNew(v === '__new__');
    if (v !== '__new__') setMoveNewFolderName('');
  };

  const confirmMove = async () => {
    let target;
    if (moveIsNew) {
      target = sanitizeFolderInput(moveNewFolderName);
    } else {
      target = moveTarget || null; // '' → null (remove from folder)
    }
    try {
      await apiService.moveMediaAsset(movingAssetId, target);
      setMovingAssetId(null);
      await Promise.all([loadFolders(), load()]);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const confirmNewFolder = async () => {
    const raw = sanitizeFolderInput(newFolderName);
    setCreatingFolder(false);
    setNewFolderName('');
    if (!raw) return;
    // If inside a real folder, create the new one as a child of it
    const parent = activeFolder && activeFolder !== '__none__' ? activeFolder : null;
    const name = parent ? `${parent}/${raw}` : raw;
    try {
      await apiService.createMediaFolder(name);
    } catch (err) {
      // Non-critical — folder still appears locally; server will confirm on next load
      console.warn('[MediaAssets] createMediaFolder failed:', err.message);
    }
    await loadFolders(); // refresh from server so the folder is truly persisted
    setActiveFolder(name);
    // Auto-expand all ancestor nodes so the new entry is visible in the tree
    const parts = name.split('/');
    if (parts.length > 1) {
      setExpandedNodes(prev => {
        const next = new Set(prev);
        for (let i = 1; i < parts.length; i++) next.add(parts.slice(0, i).join('/'));
        return next;
      });
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const uploadLabel = activeFolder && activeFolder !== '__none__'
    ? `Carica in "${activeFolder}"`
    : 'Carica Media';

  const emptyMsg = activeFolder === '__none__'
    ? 'Nessun asset senza cartella.'
    : activeFolder
      ? `Nessun asset in "${activeFolder}".`
      : 'Nessun media asset trovato.';

  return (
    <div>
      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          {lightbox.isVideo ? (
            <video src={lightbox.src} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', outline: 'none' }} onClick={e => e.stopPropagation()} />
          ) : (
            <img src={lightbox.src} alt={lightbox.fileName} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()} />
          )}
          <div style={{ color: '#ddd', fontSize: 13, marginTop: 12 }}>{lightbox.fileName}</div>
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* Header */}
      <div className="card">
        <h2>🖼️ Media Asset</h2>
        <p>Carica e gestisci le immagini usate nelle configurazioni remote dei dispositivi. I file vengono serviti tramite proxy sicuro — mai con URL pubblici.</p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="form-input"
            placeholder="Filtra per Tenant ID (opzionale)"
            value={tenantIdFilter}
            onChange={e => setTenantIdFilter(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <button
            className="btn btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            {!uploading && <Upload size={16} strokeWidth={2.2} />}
            {uploading ? 'Caricamento...' : uploadLabel}
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
      </div>

      {error && <div className="card" style={{ color: 'red' }}>❌ {error}</div>}

      {/* Two-column: tree sidebar + asset grid */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>

        {/* ── Folder tree sidebar ── */}
        <div className="card" style={{ width: 220, flexShrink: 0, padding: '12px 10px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingLeft: 4 }}>
            Cartelle
          </div>

          {/* All */}
          <SidebarRow label="Tutte" icon="🗂️" depth={0} active={activeFolder === null} onClick={() => setActiveFolder(null)} />

          {/* Recursive tree */}
          {folderTree.map(node => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              activeFolder={activeFolder}
              onSelect={setActiveFolder}
              onDelete={deleteFolder}
              expandedSet={expandedNodes}
              toggleExpand={toggleExpand}
            />
          ))}

          {/* No-folder row */}
          <div style={{ marginTop: 4, paddingTop: 6, borderTop: '1px solid #f3f4f6' }}>
            <SidebarRow label="Senza cartella" icon="📄" depth={0} active={activeFolder === '__none__'} muted onClick={() => setActiveFolder('__none__')} />
          </div>

          {/* New folder */}
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f3f4f6' }}>
            {creatingFolder ? (
              <div style={{ padding: '0 4px' }}>
                {activeFolder && activeFolder !== '__none__' && (
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={`in: ${activeFolder}`}>
                    📁 {activeFolder}/
                  </div>
                )}
                <input
                  autoFocus
                  className="form-input"
                  placeholder={activeFolder && activeFolder !== '__none__' ? 'nome sottocartella' : 'es. carousel/banners'}
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') confirmNewFolder();
                    if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
                  }}
                  style={{ width: '100%', padding: '4px 8px', fontSize: 12, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <button className="btn btn-primary" style={{ flex: 1, fontSize: 11, padding: '3px' }} onClick={confirmNewFolder}>✓</button>
                  <button className="btn btn-secondary" style={{ flex: 1, fontSize: 11, padding: '3px' }} onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}>✕</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setCreatingFolder(true)}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
                  border: '1.5px dashed #60a5fa',
                  borderRadius: 6,
                  padding: '7px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#2563eb',
                  cursor: 'pointer',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#2563eb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)'; e.currentTarget.style.borderColor = '#60a5fa'; }}
              >
                <span style={{ fontSize: 14 }}>📁</span>
                {activeFolder && activeFolder !== '__none__'
                  ? `+ Sottocartella di "${activeFolder.split('/').pop()}"`
                  : '+ Nuova cartella'}
              </button>
            )}
          </div>
        </div>

        {/* ── Asset grid ── */}
        <div className="card" style={{ flex: 1, minWidth: 0 }}>
          {/* Bulk action toolbar */}
          {selectedIds.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', flex: 1 }}>
                {selectedIds.size} selezionat{selectedIds.size === 1 ? 'o' : 'i'}
              </span>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={selectAll}>
                Seleziona tutti ({assets.length})
              </button>
              <button
                className="btn btn-danger"
                style={{ fontSize: 12, padding: '4px 12px' }}
                onClick={bulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Eliminazione...' : `🗑️ Elimina ${selectedIds.size}`}
              </button>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={clearSelection}>
                ✕ Deseleziona
              </button>
            </div>
          )}
          {loading ? (
            <p>Caricamento...</p>
          ) : assets.length === 0 ? (
            <p style={{ color: '#6b7280' }}>{emptyMsg}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {assets.map(asset => {
                const isSelected = selectedIds.has(asset.id);
                return (
                <div
                  key={asset.id}
                  style={{
                    border: `2px solid ${isSelected ? '#2563eb' : '#eee'}`,
                    borderRadius: 8, overflow: 'hidden',
                    background: isSelected ? '#eff6ff' : '#fafafa',
                    position: 'relative',
                  }}
                >
                  {/* Checkbox overlay */}
                  <div
                    onClick={() => toggleSelect(asset.id)}
                    style={{
                      position: 'absolute', top: 6, left: 6, zIndex: 2,
                      width: 20, height: 20, borderRadius: 4,
                      border: `2px solid ${isSelected ? '#2563eb' : 'rgba(255,255,255,0.85)'}`,
                      background: isSelected ? '#2563eb' : 'rgba(255,255,255,0.7)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', backdropFilter: 'blur(2px)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  >
                    {isSelected && <span style={{ color: '#fff', fontSize: 12, lineHeight: 1, fontWeight: 700 }}>✓</span>}
                  </div>
                  {asset.mimeType.startsWith('video/') ? (
                    <div
                      style={{ width: '100%', height: 120, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in', position: 'relative' }}
                      onClick={() => setLightbox({ src: apiService.getMediaPreviewUrl(asset.id), fileName: asset.fileName, isVideo: true })}
                    >
                      <video src={apiService.getMediaPreviewUrl(asset.id)} style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} preload="metadata" muted />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                        <span style={{ fontSize: 28, lineHeight: 1 }}>▶</span>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={apiService.getMediaPreviewUrl(asset.id)}
                      alt={asset.fileName}
                      style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                      loading="lazy"
                      onClick={() => setLightbox({ src: apiService.getMediaPreviewUrl(asset.id), fileName: asset.fileName })}
                    />
                  )}
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={asset.fileName}>
                      {asset.fileName}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      {formatBytes(asset.sizeBytes)} · {asset.mimeType.split('/')[1].toUpperCase()}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      {new Date(asset.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>

                    {/* Folder breadcrumb badge */}
                    {asset.folder ? (
                      <div
                        style={{ fontSize: 11, color: '#2563eb', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        title={`📁 ${asset.folder}`}
                        onClick={() => setActiveFolder(asset.folder)}
                      >
                        📁 {asset.folder}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#d1d5db', marginTop: 2 }}>📁 —</div>
                    )}

                    {/* Move panel or action row */}
                    {movingAssetId === asset.id ? (
                      <div style={{ marginTop: 8, padding: 6, background: '#f0f4ff', borderRadius: 6, border: '1px solid #c7d2fe' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: '#374151' }}>Sposta in:</div>
                        <select
                          autoFocus
                          value={moveTarget}
                          onChange={handleMoveSelectChange}
                          style={{ width: '100%', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff' }}
                        >
                          <option value="">— Nessuna cartella —</option>
                          {folders.map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                          <option value="__new__">➕ Nuova cartella...</option>
                        </select>
                        {moveIsNew && (
                          <input
                            autoFocus
                            className="form-input"
                            placeholder="es. carousel/banners"
                            value={moveNewFolderName}
                            onChange={e => setMoveNewFolderName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && confirmMove()}
                            style={{ width: '100%', marginTop: 4, padding: '4px 6px', fontSize: 12, boxSizing: 'border-box' }}
                          />
                        )}
                        <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                          <button className="btn btn-primary" style={{ flex: 1, fontSize: 11, padding: '3px' }} onClick={confirmMove}>✓ Conferma</button>
                          <button className="btn btn-secondary" style={{ flex: 1, fontSize: 11, padding: '3px' }} onClick={() => setMovingAssetId(null)}>Annulla</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                        <button className="btn btn-secondary" style={{ flex: 1, fontSize: 11, padding: '4px 4px' }} onClick={() => startMove(asset)}>
                          📁 Sposta
                        </button>
                        <button className="btn btn-danger icon-btn" style={{ flex: 1, fontSize: 11, padding: '4px 4px', gap: 2 }} onClick={() => remove(asset.id)}>
                          <DeleteIcon sx={{ fontSize: '14px !important', color: 'white' }} /> Elimina
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaAssets;
